import { DateTime } from 'luxon'

import type { Session, TemporalStatus } from './types'

/** Tolerância aplicada antes do início e após o término para considerar a sessão ao vivo. */
const LIVE_TOLERANCE_MINUTES = 15

type CheckInSession = Pick<
  Session,
  'start_at' | 'checkin_active' | 'finished'
>

/**
 * Classifica a sessão em relação ao instante atual, independente de `has_rewards`.
 *
 * A janela de tolerância existe porque a sessão presencial raramente começa e
 * termina no horário exato — o participante precisa conseguir fazer check-in
 * um pouco antes e um pouco depois.
 */
export const getTemporalStatus = (
  startAt: string,
  endAt: string
): TemporalStatus => {
  const now = DateTime.now()
  const start = DateTime.fromISO(startAt)
  const end = DateTime.fromISO(endAt)

  const isLive =
    now <= end.plus({ minutes: LIVE_TOLERANCE_MINUTES }) &&
    now >= start.minus({ minutes: LIVE_TOLERANCE_MINUTES })

  if (isLive) return 'live'
  if (now < start) return 'future'
  return 'past'
}

/**
 * Check-in fica disponível durante todo o dia da sessão (mesma data de
 * `start_at`), enquanto o produtor mantiver `checkin_active` e o participante
 * ainda não tiver concluído.
 */
export const canCheckInForSession = (
  session: CheckInSession,
  now: DateTime = DateTime.now()
): boolean => {
  if (!session.checkin_active || session.finished) return false

  const sessionDayStart = DateTime.fromISO(session.start_at).startOf('day')
  const sessionDayEnd = sessionDayStart.endOf('day')

  return now >= sessionDayStart && now <= sessionDayEnd
}
